import type { Maybe } from '@adornis/base/utilTypes.js';
import { Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';

export enum AccountingImportFileKind {
  BUCHUNGSSTAPEL = 'Buchungsstapel',
  KONTOAUSZUG = 'Kontoauszug',
}

/**
 * What the accounting import knows about one file of the tax advisor's share. The import is incremental: a
 * file is only downloaded and parsed again when its name, content tag or the parser version differ from what
 * is stored here. `_id` is a hash of the path, the same path the ids of its entries are built from.
 */
@Entity()
export class AccountingImportFile extends MongoEntity {
  public static override _class = 'AccountingImportFile';
  public static override _collectionName = 'accounting-import-files';

  @Field(() => String)
  path!: string;

  @Field(() => String)
  name!: string;

  @Field(() => String)
  kind!: AccountingImportFileKind;

  /** The school whose folder / accounting number the file belongs to. */
  @Field(() => String)
  schoolID!: string;

  /** Schools sharing the accounting number (e.g. Realschule and Gymnasium of one school). */
  @Field(() => [String], { default: v => v ?? [] })
  additionalSchoolIDs!: string[];

  /** Graph content tag of the imported version — changes whenever the content changes. */
  @Field(() => String)
  cTag: Maybe<string>;

  /** When the file was last changed in Sharepoint, as of the last listing. */
  @Field(() => Date)
  modifiedAt: Maybe<Date>;

  /** Parser version the file was imported with. Bumping `ACCOUNTING_PARSER_VERSION` re-imports every file. */
  @Field(() => Int)
  parserVersion: Maybe<number>;

  @Field(() => Date)
  importedAt: Maybe<Date>;

  @Field(() => Int)
  rowCount: Maybe<number>;

  /** Why the last import attempt failed; the file is retried on every run until it succeeds. */
  @Field(() => String)
  error: Maybe<string>;

  /**
   * Set while the file is absent from the share listing. Its entries are kept on purpose: files have vanished
   * from the listing for a night and come back, and deleting would take ids, links and resolutions with it.
   */
  @Field(() => Date)
  missingSince: Maybe<Date>;
}
